import { merge } from "lodash";

export function prepareExpression(expression: number | string) {
  if (typeof expression === "number") {
    return expression.toString();
  }
  // add quotes around regex group
  const r = /([^x0-9\s+\-/*.,]+)/g;
  return expression.replace(r, "'$1'");
}

export function unprepareExpression(expression: string) {
  // remove quotes around regex group
  return expression.replace(/['"]+/g, "");
}

export interface ExpressionItem {
  property: string;
  expression: string;
}

export function prepareVehicleCounts<T = { [key: string]: string | number }>(
  data: T & { vehicleCounts: { vehicleType: string; count: string | number }[] }
): T & { vehicleCounts: { [key: string]: string | number } } {
  const vehicleCounts = Object.values(data.vehicleCounts).reduce((acc, item) => {
    return { ...acc, [item.vehicleType]: item.count };
  }, {});
  return { ...data, vehicleCounts };
}

export function unprepareVehicleCounts<T = { [key: string]: string | number }>(
  data: T & { vehicleCounts: { [key: string]: string | number } }
): T & { vehicleCounts: { vehicleType: string; count: string | number }[] } {
  const vehicleCounts = Object.entries(data.vehicleCounts).map((pair) => {
    const [vehicleType, count] = pair;
    return { vehicleType, count };
  });
  return {
    ...data,
    vehicleCounts,
  };
}

type ExpressionListInput = { [key: string]: string | number | { [key: string]: string | number } };

export function toExpressionList<T extends ExpressionListInput = ExpressionListInput>(
  data: T,
  prefix = ""
): ExpressionItem[] {
  const propertyExpressionList = Object.keys(data).reduce((acc: ExpressionItem[], key: string) => {
    const property = [prefix, key].filter(Boolean).join(".");
    const value = data[key as keyof T];
    if ((key === "ifdmTimeFactor" || key === "roadType") && value !== "x") {
      return [...acc, { property, expression: `'${value}'` }];
    }
    if (typeof value === "object" && value !== null) {
      return [...acc, ...toExpressionList(value as ExpressionListInput, property)];
    } else {
      return [...acc, { property, expression: prepareExpression(value) }];
    }
  }, []);

  return propertyExpressionList;
}

export function toInitialValues<T>(expressionList: ExpressionItem[]): T {
  function unflatExpression(item: ExpressionItem): { [key: string]: any } {
    const [head, ...tail] = item.property.split(".");
    if (tail.length === 0) {
      return { [head]: unprepareExpression(item.expression) };
    } else {
      return { [head]: unflatExpression({ property: tail.join("."), expression: item.expression }) };
    }
  }

  const result = expressionList.reduce((acc, item: ExpressionItem) => {
    const obj = unflatExpression(item);

    // prefer merge above assign because of the shallow merge
    return merge(acc, obj);
  }, {});

  return result as T;
}
